import React, { useState } from "react";
import {
  useApp,
  useTranslate,
  FontText,
  TypingInput,
  Close,
  Icon,
  eligible,
} from "./common.jsx";
import { ContextPreview } from "./Studio.jsx";
import { encodingOptions } from "./converter.js";
import { resolvePreviewMapping } from "./preview-mapping.js";
import { PhoneGuide } from "./PhoneGuide.jsx";
export function Detail() {
  const a = useApp(),
    t = useTranslate(),
    f = a.fonts.find((f) => f.id === a.modal.id);
  const [tab, setTab] = useState("specimen"),
    [text, setText] = useState(a.sample),
    [size, setSize] = useState(56),
    [mapping, setMapping] = useState("");
  const unavailable = !f.file || f.previewUnsupported;
  const mappingInfo = resolvePreviewMapping(f);
  return (
    <>
      <div className="detailhead">
        <div>
          <p className="eyebrow">FONT SPOTLIGHT</p>
          <h2>{f.name}</h2>
          <p>
            {f.authors.join(", ")} · {f.style}
          </p>
        </div>
        <div className="toolbarrow">
          <button className="secondary" onClick={() => setTab("phone")} data-action="phone-guide">
            {t("Use on your phone", "ტელეფონზე გამოყენება")}
          </button>
          {f.file && (
            <a className="primary" href={f.file} download={f.filename}>
              <Icon name="download" />
              {t("Download font")}
            </a>
          )}
          <Close onClick={a.close} label="Close font details" />
        </div>
      </div>
      <div className="detailbody">
        <div className="tabs" role="tablist">
          {[
            ["specimen", "Specimen"],
            ["inuse", "In use"],
            ["glyphs", "Glyphs"],
            ["embed", "Embed"],
            ["phone", "Use on your phone"],
            ["about", "Details & license"],
          ].map(([v, l]) => (
            <button
              key={v}
              data-tab={v}
              role="tab"
              aria-selected={tab === v}
              className={tab === v ? "active" : ""}
              onClick={() => setTab(v)}
            >
              {v === "phone" ? t(l, "ტელეფონზე გამოყენება") : t(l)}
            </button>
          ))}
        </div>
        <div role="tabpanel">
          {unavailable && ["specimen", "inuse", "glyphs"].includes(tab) ? (
            <p className="muted">
              {t("Browser preview unavailable · Download original font")}
            </p>
          ) : tab === "specimen" ? (
            <>
              <div className="detailcontrols">
                <TypingInput
                  id="detailsample"
                  value={text}
                  onChange={setText}
                  aria-label="Specimen text"
                />
                <label className="sizecontrol">
                  <input
                    id="detailsize"
                    type="range"
                    min="20"
                    max="100"
                    value={size}
                    onChange={(e) => setSize(+e.target.value)}
                  />
                  <span id="detailpx">{size}</span>px
                </label>
              </div>
              <FontText
                font={f}
                text={text}
                mapping={mapping}
                id="detailpreview"
                className="detailpreview"
                style={{ fontSize: size }}
              />
              {!f.encoding.startsWith("Unicode") && (
                <label className="mappinglabel">
                  {t("Preview mapping")}
                  <select
                    id="previewmapping"
                    value={mapping}
                    onChange={(e) => setMapping(e.target.value)}
                  >
                    <option value="">Auto ({f.encoding})</option>
                    <option value="raw">
                      {t("Raw input / no conversion")}
                    </option>
                    {encodingOptions.map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </label>
              )}
              <div className="tags">
                {!f.encoding.startsWith("Unicode") && <span className="tag" data-tooltip={mappingInfo.missing?.length ? t("Missing letters use a fallback font: ", "გამოტოვებული ასოები სხვა შრიფტით ჩანს: ") + mappingInfo.missing.join(", ") : t("Conversion is checked separately for each font.", "კონვერტაცია თითოეული შრიფტისთვის ცალ-ცალკე მოწმდება.")}>
                  {mappingInfo.status === "verified" ? t("Mapping checked", "კონვერტაცია შემოწმებულია") : mappingInfo.status === "verified-letters" ? t("Alphabet checked", "ანბანი შემოწმებულია") : mappingInfo.status === "partial" ? t("Some glyphs missing", "ზოგი ასო აკლია") : t("Mapping needs review", "კონვერტაცია შესამოწმებელია")}
                </span>}
                {[...f.writings, ...f.classes].map((v) => (
                  <span key={v} className="tag">
                    {t(v)}
                  </span>
                ))}
              </div>
              {a.fonts.some((x) => x.family === f.family && x.id !== f.id) && (
                <>
                  <h3>{t("More styles in this family")}</h3>
                  <div className="toolbarrow">
                    {a.fonts
                      .filter((x) => x.family === f.family && x.id !== f.id)
                      .map((x) => (
                        <button
                          key={x.id}
                          className="secondary"
                          data-open={x.id}
                          onClick={() => a.open("detail", x.id)}
                        >
                          {x.name}
                        </button>
                      ))}
                  </div>
                </>
              )}
              <div className="infogrid">
                <div>
                  <dt>{t("Designer")}</dt>
                  <dd>{f.authors.join(", ")}</dd>
                </div>
                <div>
                  <dt>{t("Encoding")}</dt>
                  <dd>{f.encoding}</dd>
                </div>
                <div>
                  <dt>{t("Glyph coverage")}</dt>
                  <dd>{f.symbols}</dd>
                </div>
              </div>
            </>
          ) : tab === "inuse" ? (
            <ContextPreview font={f} />
          ) : tab === "glyphs" ? (
            <div className="glyphs">
              {(f.coverage || []).map((cp) => (
                <button
                  className="glyph"
                  key={cp}
                  data-glyph={cp}
                  onClick={() => a.copy(String.fromCodePoint(cp))}
                >
                  <FontText
                    as="span"
                    font={f}
                    text={String.fromCodePoint(cp)}
                    mapping="raw"
                  />
                  <small>
                    {cp.toString(16).toUpperCase().padStart(4, "0")}
                  </small>
                </button>
              ))}
            </div>
          ) : tab === "phone" ? (
            <PhoneGuide font={f} onEmbed={() => setTab("embed")} />
          ) : tab === "embed" ? (
            <>
              <h3>{t("Use on the web")}</h3>
              {eligible(f) ? (
                <>
                  <p>
                    {t("Choose your fonts. Copy the code. Make it Georgian.")}
                  </p>
                  <button
                    className="primary"
                    data-embed-add={f.id}
                    onClick={() => a.addEmbed(f.id)}
                  >
                    {t("Use on the web")}
                  </button>
                  <p className="muted">
                    {f.encoding.startsWith("Unicode")
                      ? "Unicode text works directly with this font."
                      : "Legacy font: your text needs the original encoding."}
                  </p>
                </>
              ) : (
                <p>{t("Embedding is restricted in this font’s metadata.")}</p>
              )}
            </>
          ) : (
            <>
              <div className="infogrid">
                {Object.entries({
                  Family: f.family,
                  Designer: f.authors.join(", "),
                  Encoding: f.encoding,
                  "Writing systems": f.writings.join(", "),
                  Classification: f.classes.join(", "),
                  Style: f.style,
                  Format: f.format,
                  "Keyboard layout": f.layout,
                  Version: f.version,
                  Characters: f.symbols,
                }).map(([k, v]) => (
                  <div key={k}>
                    <dt>{t(k)}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </div>
              <h3>{t("License & attribution")}</h3>
              <p className="license">
                {f.licenseText ||
                  f.licenseCopyright ||
                  t(
                    "No explicit license text is embedded in this font. Check with the designer before commercial use or redistribution.",
                  )}
              </p>
              {f.licenseUrl && /^https?:/.test(f.licenseUrl) && (
                <a
                  className="secondary"
                  href={f.licenseUrl}
                  target="_blank"
                  rel="noopener"
                >
                  {t("Font license")}
                </a>
              )}{" "}
              <a
                className="secondary"
                href={f.source}
                target="_blank"
                rel="noopener"
              >
                {t("Original font page")}
              </a>
            </>
          )}
        </div>
      </div>
    </>
  );
}
export function Compare() {
  const a = useApp(),
    t = useTranslate(),
    [text, setText] = useState(a.sample);
  return (
    <>
      <div className="detailhead">
        <div>
          <p className="eyebrow">SIDE BY SIDE</p>
          <h2>{t("Find your perfect fit.")}</h2>
        </div>
        <Close onClick={a.close} label="Close comparison" />
      </div>
      <div className="detailbody">
        <div className="detailcontrols">
          <TypingInput
            id="comparesample"
            value={text}
            onChange={setText}
            aria-label="Comparison text"
          />
        </div>
        <div className="comparegrid">
          {a.compare.map((id) => {
            const f = a.fonts.find((f) => f.id === id);
            return (
              <div className="compareitem" key={id}>
                <b>{f.name}</b>
                <FontText
                  className="detailpreview"
                  data-compare-preview={id}
                  font={f}
                  text={text}
                />
                <span className="tag">{f.encoding}</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
export function Embed() {
  const a = useApp(),
    t = useTranslate();
  const [text, setText] = useState("ქართული ამბები იწყება აქ"),
    [size, setSize] = useState(36),
    [mode, setMode] = useState("link"),
    [query, setQuery] = useState(""),
    [selected, setSelected] = useState("905");
  const available = a.fonts.filter(eligible),
    fonts = a.embed.map((id) => a.fonts.find((f) => f.id === id)),
    origin = "https://anbani-font-cdn.snowy-rain-00a8.workers.dev";
  const url = `${origin}/css?family=${a.embed.join(",")}&display=swap`;
  const head = fonts.length
    ? mode === "import"
      ? `@import url('${url}');`
      : `<link rel="preconnect" href="${origin}" crossorigin>\n<link rel="stylesheet" href="${url.replace("&", "&amp;")}">`
    : t("Choose a font to generate your embed code.");
  const usage = fonts
    .map(
      (f) =>
        `${fonts.length > 1 ? ".font-" + f.id : "body"} {\n  font-family: '${f.webFamily || f.name}', sans-serif;\n  font-weight: ${f.webWeight || 400};\n  font-style: ${f.webStyle || "normal"};\n}`,
    )
    .join("\n\n");
  const options = available.filter((f) =>
    (f.name + " " + f.authors.join(" "))
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  return (
    <>
      <div className="detailhead">
        <div>
          <p className="eyebrow">FONTLAB · WEB FONTS</p>
          <h2>{t("Use on the web")}</h2>
          <p>{t("Choose your fonts. Copy the code. Make it Georgian.")}</p>
        </div>
        <Close onClick={a.close} label="Close embed panel" />
      </div>
      <div className="detailbody">
        <div className="embedlayout embedstudio">
          <section className="embedvisual">
            <div className="embedsectionhead">
              <div>
                <p className="eyebrow">{t("YOUR FONT COLLECTION")}</p>
                <h3>{t("See it. Make it yours.")}</h3>
              </div>
              <span className="selectioncount">{fonts.length} / 20</span>
            </div>
            <div className="embedsamplecontrols">
              <label htmlFor="embedsample">{t("Preview text")}</label>
              <TypingInput id="embedsample" value={text} onChange={setText} />
              <div className="embedsizecontrol">
                <label htmlFor="embedsize">{t("Size")}</label>
                <input
                  id="embedsize"
                  type="range"
                  min="20"
                  max="72"
                  value={size}
                  onChange={(e) => setSize(+e.target.value)}
                />
                <output>{size}px</output>
              </div>
            </div>
            <div className="embedfontlist">
              {fonts.map((f) => (
                <article className="embedfontcard" key={f.id}>
                  <div className="embedfonthead">
                    <div>
                      <h4 className="cardname">{f.name}</h4>
                      <p className="author">{f.authors.join(", ")}</p>
                    </div>
                    <button
                      className="iconbutton"
                      data-embed-remove={f.id}
                      aria-label={"Remove " + f.name}
                      onClick={() =>
                        a.setEmbed(a.embed.filter((id) => id !== f.id))
                      }
                    >
                      <Icon name="close" />
                    </button>
                  </div>
                  <FontText
                    className="embed-sample"
                    font={f}
                    text={text || "ქართული დამწერლობა"}
                    style={{ fontSize: size }}
                  />
                  <div className="embedfontmeta">
                    <span
                      className={
                        "tag " +
                        (f.encoding.startsWith("Unicode") ? "unicode" : "")
                      }
                    >
                      {f.encoding}
                    </span>
                    <span>
                      {f.style} · {f.webWeight || 400}
                    </span>
                    <span>WOFF2</span>
                  </div>
                  {!f.encoding.startsWith("Unicode") && (
                    <p className="legacyhint">
                      {t(
                        "Legacy font: website text needs the original encoding.",
                      )}
                    </p>
                  )}
                </article>
              ))}
              {!fonts.length && (
                <div className="embedempty">
                  {t("Your selected fonts will appear here.")}
                </div>
              )}
            </div>
            <details className="embedadd">
              <summary>{t("Add another font")}</summary>
              <div className="embedpicker">
                <input
                  id="embedsearch"
                  type="search"
                  value={query}
                  placeholder={t("Find a font or designer…")}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <select
                  id="embedselect"
                  value={
                    options.some((f) => f.id === selected)
                      ? selected
                      : options[0]?.id || ""
                  }
                  onChange={(e) => setSelected(e.target.value)}
                >
                  {options.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} · {f.style}
                    </option>
                  ))}
                </select>
                <button
                  className="secondary"
                  data-action="addembed"
                  onClick={() =>
                    a.addEmbed(
                      options.some((f) => f.id === selected)
                        ? selected
                        : options[0]?.id,
                    )
                  }
                >
                  {t("Add font")}
                </button>
              </div>
            </details>
          </section>
          <section className="embedcode">
            <div className="embedsectionhead">
              <div>
                <p className="eyebrow">{t("FROM PREVIEW TO PRODUCTION")}</p>
                <h3>{t("On your website.")}</h3>
              </div>
              <span className="cdnstatus">
                <i />
                Cloudflare CDN
              </span>
            </div>
            <div className="embedstep">
              <span className="stepnumber">1</span>
              <div>
                <h4>{t("Connect your fonts")}</h4>
                <p className="muted">
                  {t("One link for your entire selection.")}
                </p>
              </div>
            </div>
            <div className="embedformat">
              <label htmlFor="embedmode">{t("Code format")}</label>
              <select
                id="embedmode"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
              >
                <option value="link">HTML link</option>
                <option value="import">CSS @import</option>
              </select>
            </div>
            <p className="muted" id="embedinstruction">
              {t(
                mode === "import"
                  ? "Place @import at the beginning of your stylesheet."
                  : "Place the stylesheet link inside your page’s <head>.",
              )}
            </p>
            <pre className="codeblock" id="embedhead">
              {head}
            </pre>
            <button
              className="primary embedcopy"
              disabled={!fonts.length}
              data-action="copyembed"
              onClick={() => a.copy(head)}
            >
              <Icon name="copy" />
              {t("Copy embed code")}
            </button>
            <div className="embedstep">
              <span className="stepnumber">2</span>
              <div>
                <h4>{t("Give your text a voice")}</h4>
                <p className="muted">
                  {t("Apply the font in your stylesheet.")}
                </p>
              </div>
            </div>
            <pre id="embedusage" className="codeblock">
              {usage || t("Your font-family CSS will appear here.")}
            </pre>
            <button
              className="secondary embedcopy"
              disabled={!fonts.length}
              data-action="copyusage"
              onClick={() => a.copy(usage)}
            >
              <Icon name="copy" />
              {t("Copy CSS")}
            </button>
            <div className="embedfootnote">
              <b>{t("Made for the web")}</b>
              <p>
                {t(
                  "Lightweight WOFF2 files. Text stays visible while fonts load.",
                )}
              </p>
              <p>{t("Check each designer’s license before publishing.")}</p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
